import React, { useState } from "react";
import { humanizeKey, formatSmart, isMetricLike, resolveDrilldown } from "../../lib/dashboardFormat";
import DrilldownModal, { type DrilldownTarget } from "./DrilldownModal";

// `drilldown` is excluded from the generic per-key rendering below because
// it's handled specially: a *top-level* `{endpoint, params}` on the whole
// response (as opposed to one nested inside an individual metric/row) is
// the "see everything behind this screen" pointer, rendered as a single
// link rather than its own ugly nested KPI section.
const META_KEYS = new Set(["filters", "comparison", "note", "period", "generated_at", "drilldown"]);

// Generic renderer for MVP-E's `GET /dashboard/*` responses. The API
// contract (specs/api_contract_mvp_e.md) is strict about *behavior*
// (every KPI drills down, comparison blocks mirror the main shape) but
// deliberately doesn't pin an exact JSON schema per endpoint in the
// openapi doc — so instead of hardcoding field names that could drift out
// of sync with the backend agent's implementation, this renders whatever
// keys actually come back: flat primitives/`{value, drilldown}` metrics as
// KPI tiles, arrays as tables, and one level of nested objects as their
// own labelled subsection. Every clickable tile/row opens the real
// underlying MVP-A/B/C/D records via DrilldownModal (test #30).
export default function AutoDashboard({
  data,
  hideKeys,
  emptyLabel = "Sin datos para este período todavía.",
}: {
  data: Record<string, any> | null | undefined;
  hideKeys?: string[];
  emptyLabel?: string;
}) {
  const [drill, setDrill] = useState<DrilldownTarget | null>(null);

  if (!data) return null;
  const hidden = new Set(hideKeys || []);
  const entries = Object.entries(data).filter(([k, v]) => !META_KEYS.has(k) && !hidden.has(k) && v !== undefined);

  const kpiEntries = entries.filter(([, v]) => v === null || typeof v === "number" || typeof v === "string" || typeof v === "boolean" || isMetricLike(v));
  const tableEntries = entries.filter(([, v]) => Array.isArray(v) && v.length > 0);
  const emptyTableEntries = entries.filter(([, v]) => Array.isArray(v) && v.length === 0);
  const nestedEntries = entries.filter(([, v]) => v !== null && typeof v === "object" && !Array.isArray(v) && !isMetricLike(v));

  function openDrill(title: string, ddRaw: any, rows?: any[]) {
    const dd = resolveDrilldown(ddRaw);
    if (rows) {
      setDrill({ title, endpoint: "(inline)", rows });
    } else if (dd) {
      setDrill({ title, endpoint: dd.endpoint, params: dd.params });
    }
  }

  const allEmpty = kpiEntries.length === 0 && tableEntries.length === 0 && nestedEntries.length === 0;
  const topDrilldown = resolveDrilldown(data.drilldown);

  return (
    <div>
      {allEmpty && <p className="meta">{emptyLabel}</p>}

      {topDrilldown && (
        <button className="link-btn" style={{ marginBottom: 8 }} onClick={() => openDrill("Registros fuente", data.drilldown)}>
          Ver registros fuente de este panel →
        </button>
      )}

      {kpiEntries.length > 0 && (
        <div className="dashboard-metrics">
          {kpiEntries.map(([key, raw]) => {
            const metric = isMetricLike(raw) ? raw : { value: raw };
            const dd = resolveDrilldown((metric as any).drilldown);
            const rows = (metric as any).rows;
            const clickable = !!dd || (Array.isArray(rows) && rows.length > 0);
            const Tag = clickable ? "button" : "div";
            return (
              <Tag
                key={key}
                className="metric-tile"
                style={clickable ? undefined : { cursor: "default" }}
                onClick={clickable ? () => openDrill(metric.label || humanizeKey(key), (metric as any).drilldown, rows) : undefined}
              >
                <div className="label">{metric.label || humanizeKey(key)}</div>
                <div className="value">{formatSmart(key, metric.value)}</div>
              </Tag>
            );
          })}
        </div>
      )}

      {tableEntries.map(([key, rows]) => (
        <DashboardTable key={key} title={humanizeKey(key)} rows={rows as any[]} onRowDrilldown={openDrill} />
      ))}

      {emptyTableEntries.map(([key]) => (
        <div className="section-card" key={key}>
          <h2>{humanizeKey(key)}</h2>
          <p className="meta">Sin registros.</p>
        </div>
      ))}

      {nestedEntries.map(([key, obj]) => (
        <div className="section-card" key={key}>
          <h2>{humanizeKey(key)}</h2>
          <AutoDashboard data={obj as Record<string, any>} />
        </div>
      ))}

      {drill && <DrilldownModal target={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

// `key?:` here is TS-only (matches router.tsx's Link pattern) — it lets
// call sites pass `key={...}` for React's list-reconciliation without the
// component itself destructuring/consuming it, which would otherwise
// trigger React's "`key` is not a prop" dev warning.
function DashboardTable({
  title,
  rows,
  onRowDrilldown,
}: {
  key?: string;
  title: string;
  rows: any[];
  onRowDrilldown: (title: string, dd: any, rows?: any[]) => void;
}) {
  const columns = React.useMemo(() => {
    const keys = new Set<string>();
    rows.slice(0, 25).forEach((r) => {
      if (r && typeof r === "object") Object.keys(r).forEach((k) => k !== "drilldown" && keys.add(k));
    });
    return Array.from(keys)
      .filter((k) => {
        const sample = rows.find((r) => r && r[k] !== undefined)?.[k];
        return typeof sample !== "object" || sample === null;
      })
      .slice(0, 9);
  }, [rows]);

  return (
    <div className="section-card">
      <h2>{title}</h2>
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{humanizeKey(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((r, i) => {
            const dd = resolveDrilldown(r?.drilldown);
            const clickable = !!dd;
            return (
              <tr
                key={r?.id ?? i}
                className={clickable ? "link-row" : undefined}
                style={clickable ? { cursor: "pointer" } : undefined}
                onClick={clickable ? () => onRowDrilldown(r?.name || r?.label || title, r.drilldown) : undefined}
              >
                {columns.map((c) => (
                  <td key={c}>{formatSmart(c, r?.[c])}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > 50 && <p className="meta">Mostrando 50 de {rows.length}.</p>}
    </div>
  );
}
